import { useMemo } from 'react';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import map from 'lodash/map';
import { Trans } from 'react-i18next';
import Dialog, { Clip, Close } from '../molecules/Dialog';
import ActiveConceptItem from '../molecules/ActiveConceptItem';
import InactiveConceptItem from '../molecules/InactiveConceptItem';
import Tab from '../atoms/Tab';
import Tabs from '../atoms/Tabs';
import Grid from '../atoms/Grid';
import {
	containsConcept,
	getCategories,
	getConceptsByCategory
} from '../../data/Concepts';
import type { CategoryKey, Concept } from '../../data/Concepts';

const Title = styled.span`
	color: ${(props) => props.theme.colors.white};
	font: ${(props) => props.theme.font.title};
	font-size: 8.6vmin;
	left: 8%;
	overflow: hidden;
	position: absolute;
	right: 8%;
	text-overflow: ellipsis;
	top: 6%;
	white-space: nowrap;
	text-shadow: ${(props) => props.theme.shadow.title};
`;

const TabContent = styled.div`
	top: 30%;
	background-color: ${(props) => props.theme.colors.veryLightBlue};
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	overflow: hidden;
	padding-left: 5.2vw;
	padding-right: 5.2vw;
	padding-top: 4.2vh;
	padding-bottom: 4.2vh;
`;

export interface Props {
	concepts: Concept[];
	activeConcepts: Concept[];
	/**
	 * If not set, all concepts are enabled
	 */
	enabledConcepts?: Concept[];
	currentCategory?: CategoryKey;
	onCurrentCategoryChange: (category: CategoryKey) => void;
	onClose: () => void;
	onConcept: (concept: Concept) => void;
	onActivate: (concept: Concept) => void;
}

const ConceptsDialog: FunctionComponent<Props> = (props: Props) => {
	const {
		concepts,
		activeConcepts,
		enabledConcepts,
		currentCategory = concepts[0].category,
		onCurrentCategoryChange,
		onClose,
		onConcept,
		onActivate
	} = props;

	const categoryConcepts = getConceptsByCategory(concepts, currentCategory);

	const conceptComponents = useMemo(() => {
		const hasDisabledConcepts = enabledConcepts !== undefined;
		const highlightInactiveConcepts = hasDisabledConcepts;
		return map(categoryConcepts, (concept) => {
			const isActive = containsConcept(activeConcepts, concept);
			const isDisabled =
				enabledConcepts !== undefined &&
				!containsConcept(enabledConcepts, concept);
			if (isActive) {
				return (
					<ActiveConceptItem
						key={concept.name}
						concept={concept}
						disabled={isDisabled}
						onConcept={onConcept}
					/>
				);
			} else {
				return (
					<InactiveConceptItem
						key={concept.name}
						concept={concept}
						disabled={isDisabled}
						onConcept={onConcept}
						onActivate={onActivate}
						highlighted={highlightInactiveConcepts && !isDisabled}
					/>
				);
			}
		});
	}, [
		categoryConcepts,
		activeConcepts,
		enabledConcepts,
		onConcept,
		onActivate
	]);

	const categoryComponents = useMemo(
		() =>
			map(getCategories(), (category) => {
				return (
					<Tab key={category} value={category}>
						<Trans>{category}</Trans>
					</Tab>
				);
			}),
		[]
	);

	return (
		<Dialog light closeButton={<Close onClose={onClose} />}>
			<Clip>
				<Tabs
					value={currentCategory}
					onChange={onCurrentCategoryChange}
					position="absolute"
					left="6%"
					right="6%"
					bottom="70%"
				>
					{categoryComponents}
				</Tabs>
				<TabContent>
					<Grid columns={2} rows={2} width="100%" height="100%">
						{conceptComponents}
					</Grid>
				</TabContent>
				<Title>
					<Trans>concepts.title</Trans>
				</Title>
			</Clip>
		</Dialog>
	);
};

export default ConceptsDialog;
